import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog as HuiDialog, Transition } from "@headlessui/react";
import { useModalStackLayer } from "@/lib/modalStack";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { canViewHref } from "@/lib/acl";
import { isNewDesignEnabled } from "@/lib/designPreview";
import { MAIN_NAV, resolveHref } from "@/lib/nav";
import type { NavItem } from "@/lib/nav";
import { resolveBestellwesenNavItems } from "@/lib/bestellwesenNav";
import { DOKUMENTE_HUB_BADGE_KEYS } from "@/lib/dokumenteNav";
import { isNavItemActive, resolvePersonalNavItems } from "@/lib/personalNav";
import type { NavBadgeKey } from "@/lib/navBadges";
import { AccountMenu } from "@/components/AccountMenu";
import { TourMenu } from "@/features/tours/TourMenu";
import { DevRoleSwitcher } from "@/components/DevRoleSwitcher";
import { NavBadgeGroup } from "@/components/NavBadge";
import { RealtimeStatusIndicator } from "@/components/RealtimeStatusIndicator";
import { AppHealthIndicator } from "@/components/AppHealthIndicator";
import { ThemeToggle } from "@/theme/ThemeToggle";
import { usePageHeader } from "@/layout/usePageHeader";
import { classNames } from "@/lib/format";
import { useNavBadges } from "@/hooks/useNavBadges";
import type { NavBadgeCounts } from "@/lib/navBadges";
import { VacationHeaderDecor, VacationNavDecor } from "@/components/VacationHeaderDecor";
import { useVacation } from "@/features/vacation/VacationProvider";
import { ChristmasHeaderDecor, ChristmasNavLights } from "@/components/ChristmasHeaderDecor";
import { HalloweenHeaderDecor, HalloweenNavLights } from "@/components/HalloweenHeaderDecor";
import { isChristmasSeason, isHalloweenSeason } from "@/lib/season";
import {
  decoAllowsChristmasDecor,
  decoAllowsHalloweenDecor,
  decoAllowsHalloweenFullDecor,
} from "@/lib/decoPreference";
import { useDecoPreference } from "@/features/deco/DecoPreferenceProvider";
import { useSiteHeaderStickyOffset } from "@/hooks/useSiteHeaderStickyOffset";

/** Ernsthaus-Logo aus der klassischen App (`static/partials/header.js`). */
const ERNSTHAUS_LOGO = `/static/${encodeURIComponent("Ernsthaus Logo.png")}`;

export function Header() {
  const headerRef = useRef<HTMLElement>(null);
  const { user } = useCurrentUser();
  const { meta } = usePageHeader();
  const [allowed, setAllowed] = useState<NavItem[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newDesign, setNewDesign] = useState(() => isNewDesignEnabled());

  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const userLoaded = !!user;
  const badgeCounts = useNavBadges(user);

  useEffect(() => {
    let cancelled = false;
    if (!userLoaded) {
      setAllowed([]);
      return;
    }
    (async () => {
      const bueroNav = userIsBuero(user);
      const checks = await Promise.all(
        MAIN_NAV.map((item) => canViewHref(item.aclHref ?? item.href, groups)),
      );
      // Büro: Angebote, Nachweise & Buchhaltung liegen unter Dokumente (linke Navigation).
      const allowedMain = MAIN_NAV.filter((item, idx) => {
        if (!checks[idx]) return false;
        if (bueroNav && (item.key === "angebotsanfragen" || item.key === "timesheets")) {
          return false;
        }
        return true;
      }).map((item) => {
        if (!bueroNav || item.key !== "dokumente") return item;
        return {
          ...item,
          badgeKeys: [...DOKUMENTE_HUB_BADGE_KEYS] as NavBadgeKey[],
        };
      });
      const [personalItems, bestellwesenItems] = await Promise.all([
        resolvePersonalNavItems(user, groups),
        resolveBestellwesenNavItems(groups),
      ]);
      const supportIdx = allowedMain.findIndex((item) => item.key === "support");
      const bestellInsertAt = supportIdx >= 0 ? supportIdx + 1 : 0;
      const adminIdx = allowedMain.findIndex((item) => item.key === "admin");
      const personalInsertAt = adminIdx >= 0 ? adminIdx + 1 : allowedMain.length;
      const merged = [
        ...allowedMain.slice(0, bestellInsertAt),
        ...bestellwesenItems,
        ...allowedMain.slice(bestellInsertAt, personalInsertAt),
        ...personalItems,
        ...allowedMain.slice(personalInsertAt),
      ];
      if (!cancelled) setAllowed(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [userLoaded, groups, user]);

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      setNewDesign(detail?.enabled ?? isNewDesignEnabled());
    };
    window.addEventListener("eh:new-design-change", onChange);
    return () => window.removeEventListener("eh:new-design-change", onChange);
  }, []);

  const { preference: decoPreference } = useDecoPreference();
  const { showVacationDecor } = useVacation();
  const pageTitle = meta?.title?.trim() || "Ernsthaus App";
  // Reihenfolge wie im BirthdayProvider: Urlaub > Halloween > Weihnachten.
  const halloweenSeason = !showVacationDecor && isHalloweenSeason(user);
  const halloweenMarks = halloweenSeason && decoAllowsHalloweenDecor(decoPreference);
  const halloweenFull = halloweenSeason && decoAllowsHalloweenFullDecor(decoPreference);
  const christmasDecor =
    !showVacationDecor &&
    !halloweenSeason &&
    isChristmasSeason(user) &&
    decoAllowsChristmasDecor(decoPreference);

  useSiteHeaderStickyOffset(headerRef, [
    pageTitle,
    meta?.subtitle,
    christmasDecor,
    halloweenMarks,
    halloweenFull,
    showVacationDecor,
    allowed.length,
    drawerOpen,
  ]);

  return (
    <header
      ref={headerRef}
      className={clsx(
        "eh-site-header sticky top-0 z-40 border-b border-separator bg-app/90 backdrop-blur-md",
        showVacationDecor && "eh-site-header--vacation relative",
        christmasDecor && "eh-site-header--christmas relative",
        halloweenMarks && "eh-site-header--halloween relative",
      )}
    >
      {showVacationDecor ? <VacationHeaderDecor /> : null}
      {christmasDecor ? <ChristmasHeaderDecor /> : null}
      {halloweenMarks ? (
        <HalloweenHeaderDecor intensity={halloweenFull ? "full" : "subtle"} />
      ) : null}
      {/* Zeile 1: Logo · Seitentitel · Aktionen */}
      <div className="relative z-20 mx-auto max-w-7xl px-4 py-3 sm:px-6">
        <div className="flex min-h-[3.5rem] items-center gap-3 lg:grid lg:min-h-[4.75rem] lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-8">
          <Link
            to="/"
            className="shrink-0 rounded-md outline-none ring-offset-2 ring-offset-app focus-visible:ring-2 focus-visible:ring-accent"
            aria-label="Zurück zur Startübersicht"
          >
            <img
              src={ERNSTHAUS_LOGO}
              alt="Ernsthaus"
              className="h-9 w-auto max-w-[180px] object-contain object-left sm:h-10 md:h-12 lg:h-[3.25rem]"
              width={200}
              height={64}
              loading="eager"
              decoding="async"
            />
          </Link>

          <div className="min-w-0 flex-1 text-center lg:px-6">
            <h1 className="text-base font-bold leading-tight tracking-tight text-label sm:text-lg md:text-xl lg:text-[clamp(1.125rem,.95rem+.9vw,1.75rem)]">
              <span className="line-clamp-2">{pageTitle}</span>
            </h1>
            {meta?.subtitle ? (
              <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-label-secondary sm:text-sm">
                {meta.subtitle}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <RealtimeStatusIndicator className="hidden sm:inline-flex" />
            <AppHealthIndicator />
            <TourMenu />
            <DevRoleSwitcher />
            <AccountMenu user={user} />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-label transition hover:bg-fill md:hidden"
              aria-label="Navigation öffnen"
            >
              <MenuIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Zeile 2: Navigation – iOS-Style mit Pillen */}
      <nav
        aria-label="Hauptnavigation"
        className={clsx(
          "relative z-10 hidden border-t border-separator md:block",
          showVacationDecor && "eh-site-header__nav--vacation",
          christmasDecor && "eh-site-header__nav--christmas",
          halloweenFull && "eh-site-header__nav--halloween",
        )}
      >
        {showVacationDecor ? <VacationNavDecor /> : null}
        {christmasDecor ? <ChristmasNavLights /> : null}
        {halloweenFull ? <HalloweenNavLights /> : null}
        <div className="relative z-[1] mx-auto flex max-w-7xl flex-wrap justify-center gap-x-1 gap-y-1.5 px-3 py-2 sm:gap-x-1.5 sm:px-4 lg:gap-x-2 lg:py-2.5">
          {allowed.map((item) => (
            <NavItemLink key={item.key} item={item} newDesign={newDesign} badgeCounts={badgeCounts} />
          ))}
        </div>
      </nav>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={allowed}
        newDesign={newDesign}
        badgeCounts={badgeCounts}
      />
    </header>
  );
}

function NavItemLink({
  item,
  newDesign,
  badgeCounts,
}: {
  item: NavItem;
  newDesign: boolean;
  badgeCounts: NavBadgeCounts;
}) {
  const location = useLocation();
  const baseClass =
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium text-label-secondary transition sm:gap-2 sm:text-sm hover:bg-fill hover:text-label";
  const activeClass = "bg-accent text-label-on-accent shadow-sm hover:bg-accent-hover hover:text-label-on-accent";
  const isActive = isNavItemActive(item, location.pathname);
  const label = (
    <>
      <span>{item.label}</span>
      <NavBadgeGroup
        badgeKeys={item.badgeKeys}
        badgeSumKeys={item.badgeSumKeys}
        counts={badgeCounts}
      />
    </>
  );

  if (item.internal && newDesign) {
    return (
      <NavLink
        to={item.href}
        className={classNames(baseClass, isActive && activeClass)}
        end={item.href === "/"}
      >
        {label}
      </NavLink>
    );
  }

  return (
    <a href={resolveHref(item, newDesign)} className={classNames(baseClass, isActive && activeClass)}>
      {label}
    </a>
  );
}

function MobileDrawer({
  open,
  onClose,
  items,
  newDesign,
  badgeCounts,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  newDesign: boolean;
  badgeCounts: NavBadgeCounts;
}) {
  const location = useLocation();
  const { zIndex: stackZIndex } = useModalStackLayer(open);
  return (
    <Transition.Root show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className="relative z-50 md:hidden"
        style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-y-0 left-0 flex max-w-full">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <HuiDialog.Panel className="flex w-72 flex-col bg-card shadow-ios-md">
              <div className="flex h-14 items-center justify-between border-b border-separator px-4">
                <span className="text-base font-semibold text-label">Navigation</span>
                <button
                  type="button"
                  className="rounded-md p-1 text-label-secondary hover:bg-fill-tertiary"
                  onClick={onClose}
                  aria-label="Schließen"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                {items.map((item) => {
                  const isActive = item.internal && newDesign && isNavItemActive(item, location.pathname);
                  const linkClass = classNames(
                    "flex items-center justify-between gap-2 px-4 py-2 text-sm font-medium transition",
                    isActive
                      ? "bg-accent-soft text-accent"
                      : "text-label hover:bg-fill-tertiary",
                  );
                  const label = (
                    <>
                      <span>{item.label}</span>
                      <NavBadgeGroup
                        badgeKeys={item.badgeKeys}
                        badgeSumKeys={item.badgeSumKeys}
                        counts={badgeCounts}
                      />
                    </>
                  );
                  if (item.internal && newDesign) {
                    return (
                      <Link key={item.key} to={item.href} className={linkClass} onClick={onClose}>
                        {label}
                      </Link>
                    );
                  }
                  return (
                    <a key={item.key} href={resolveHref(item, newDesign)} className={linkClass}>
                      {label}
                    </a>
                  );
                })}
              </div>
            </HuiDialog.Panel>
          </Transition.Child>
        </div>
      </HuiDialog>
    </Transition.Root>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
